/**
 * Amounts like Grunderwerbsteuer or Maklerprovision are entered either as a
 * percentage of a reference value (the purchase price) or as a fixed euro
 * amount. The unit is stored with the value, so a euro amount stays fixed when
 * the reference changes.
 */
export type AmountUnit = App.Enums.EstateObject.AmountUnit

/** Beträge leben in Cent. Ohne diese Rundung tragen berechnete Summen Fließkomma-Rauschen. */
export const roundToCents = (value: number): number => Math.round(value * 100) / 100

export const asEuro = (value: number | null, unit: AmountUnit, reference: number | null): number | null => {
  if (unit === 'euro') {
    return value
  }

  return value === null || !reference ? null : roundToCents((value / 100) * reference)
}

export const asPercent = (value: number | null, unit: AmountUnit, reference: number | null): number | null => {
  if (unit === 'percent') {
    return value
  }

  return value === null || !reference ? null : roundToCents((value / reference) * 100)
}

/** Keeps the represented amount as close as possible while switching the unit. */
export const convert = (
  value: number | null,
  from: AmountUnit,
  to: AmountUnit,
  reference: number | null,
): number | null => {
  const converted = to === 'euro' ? asEuro(value, from, reference) : asPercent(value, from, reference)

  return converted ?? value
}

/** The same amount expressed in the other unit — shown as the ≈ hint next to an amount field. */
export const counterpart = (
  value: number | null,
  unit: AmountUnit,
  reference: number | null,
): { value: number; unit: AmountUnit } | null => {
  const other: AmountUnit = unit === 'percent' ? 'euro' : 'percent'
  const converted = other === 'euro' ? asEuro(value, unit, reference) : asPercent(value, unit, reference)

  return converted === null ? null : { value: converted, unit: other }
}
